import { useGlobalSearchParams } from 'expo-router'
import { useState } from 'react'
import { View } from 'react-native'
import { Menu } from 'react-native-paper'
import { StyleSheet } from 'react-native-unistyles'

import SaveStatus from '@/components/shared/SaveStatus'
import UniButton from '@/components/unistyles/UniButton'
import UniIconButton from '@/components/unistyles/UniIconButton'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useFosterActions } from '@/hooks/useFosterActions'
import FosterService from '@/services/fosterService'

const FosterDetailHeaderContent = () => {
  const { isSmallScreen } = useBreakpoint()

  // This header lives outside of the FosterDetail component, and useLocalSearchParams won't re-render on navigation changes up here.
  // So instead of using FosterService.useCurrentFoster() we use useGlobalSearchParams to get the fosterId from the URL.
  const { id: fosterId } = useGlobalSearchParams<{ id: string }>()

  const { foster } = FosterService.useFoster(fosterId)
  const isSaving = FosterService.useIsSaving()

  const [menuVisible, setMenuVisible] = useState(false)
  const [printMenuVisible, setPrintMenuVisible] = useState(false)

  const { actions, isSubscriptionActive } = useFosterActions(fosterId)

  const saveStatusComponent = (
    <SaveStatus
      isSaving={isSaving}
      updatedAt={foster?.updatedAt}
      updatedBy={`${foster?.updatedBy?.firstName} ${foster?.updatedBy?.lastName}`}
    />
  )

  if (!isSubscriptionActive) {
    return saveStatusComponent
  }

  const scanAction = actions.find((a) => a.id === 'scan-documents')
  const printCoverAction = actions.find((a) => a.id === 'print-cover-sheet')
  const printContractAction = actions.find(
    (a) => a.id === 'print-adoption-contract'
  )
  const deleteAction = actions.find((a) => a.id === 'delete')
  const markDeceasedAction = actions.find((a) => a.id === 'mark-deceased')
  const copyAction = actions.find((a) => a.id === 'copy-foster')

  const dismissMenu = () => setMenuVisible(false)

  if (isSmallScreen) {
    return (
      <View style={styles.container}>
        {saveStatusComponent}
        <Menu
          anchor={
            <UniIconButton
              icon="dots-vertical"
              onPress={() => setMenuVisible(true)}
              testID="foster-actions-menu-button"
            />
          }
          anchorPosition="bottom"
          onDismiss={dismissMenu}
          visible={menuVisible}
        >
          {scanAction && (
            <Menu.Item
              leadingIcon={scanAction.icon}
              onPress={() => {
                dismissMenu()
                scanAction.onPress()
              }}
              title={scanAction.label}
            />
          )}
          {printCoverAction && (
            <Menu.Item
              leadingIcon={printCoverAction.icon}
              onPress={() => {
                dismissMenu()
                printCoverAction.onPress()
              }}
              title={printCoverAction.label}
            />
          )}
          {printContractAction && (
            <Menu.Item
              leadingIcon={printContractAction.icon}
              onPress={() => {
                dismissMenu()
                printContractAction.onPress()
              }}
              title={printContractAction.label}
            />
          )}
          {copyAction && (
            <Menu.Item
              leadingIcon={copyAction.icon}
              onPress={() => {
                dismissMenu()
                copyAction.onPress()
              }}
              title={copyAction.label}
            />
          )}
          {markDeceasedAction && (
            <Menu.Item
              leadingIcon={markDeceasedAction.icon}
              onPress={() => {
                dismissMenu()
                markDeceasedAction.onPress()
              }}
              title={markDeceasedAction.label}
            />
          )}
          {deleteAction && (
            <Menu.Item
              leadingIcon={deleteAction.icon}
              onPress={() => {
                dismissMenu()
                deleteAction.onPress()
              }}
              title={deleteAction.label}
              titleStyle={{ color: deleteAction.color }}
            />
          )}
        </Menu>
      </View>
    )
  }

  return (
    <>
      <View style={styles.container}>
        {/* Scan Documents Button */}
        {scanAction && (
          <UniButton
            compact
            icon={scanAction.icon}
            mode="text"
            onPress={() => scanAction.onPress()}
            style={styles.actionButton}
            testID={scanAction.testID}
          >
            {scanAction.label}
          </UniButton>
        )}

        {/* Print button with dropdown */}
        {(printCoverAction || printContractAction) && (
          <Menu
            anchor={
              <UniButton
                compact
                icon="chevron-down"
                mode="text"
                onPress={() => setPrintMenuVisible(true)}
                style={styles.actionButton}
                testID="print-menu-button"
              >
                Print
              </UniButton>
            }
            anchorPosition="bottom"
            onDismiss={() => setPrintMenuVisible(false)}
            visible={printMenuVisible}
          >
            {printCoverAction && (
              <Menu.Item
                leadingIcon={printCoverAction.icon}
                onPress={() => {
                  setPrintMenuVisible(false)
                  printCoverAction.onPress()
                }}
                title={printCoverAction.label}
              />
            )}
            {printContractAction && (
              <Menu.Item
                leadingIcon={printContractAction.icon}
                onPress={() => {
                  setPrintMenuVisible(false)
                  printContractAction.onPress()
                }}
                title={printContractAction.label}
              />
            )}
          </Menu>
        )}

        {/* Delete Button */}
        {deleteAction && (
          <UniButton
            compact
            icon={deleteAction.icon}
            mode="text"
            onPress={() => deleteAction.onPress()}
            style={styles.actionButton}
            testID={deleteAction.testID}
            textColor={deleteAction.color}
          >
            {deleteAction.label}
          </UniButton>
        )}

        {/* Mark Deceased Button */}
        {markDeceasedAction && (
          <UniButton
            compact
            icon={markDeceasedAction.icon}
            mode="text"
            onPress={() => markDeceasedAction.onPress()}
            style={styles.actionButton}
            testID={markDeceasedAction.testID}
          >
            {markDeceasedAction.label}
          </UniButton>
        )}

        {/* Copy Foster Button */}
        {copyAction && (
          <UniButton
            compact
            icon={copyAction.icon}
            mode="text"
            onPress={() => copyAction.onPress()}
            style={styles.actionButton}
            testID={copyAction.testID}
          >
            {copyAction.label}
          </UniButton>
        )}

        {saveStatusComponent}
      </View>
    </>
  )
}

const styles = StyleSheet.create(() => ({
  actionButton: {
    marginRight: 8,
  },
  container: {
    alignItems: 'center',
    flexDirection: 'row',
  },
}))

export default FosterDetailHeaderContent
